import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout, { PageHeader } from "@/components/Layout";
import { api, fmtINR, fmtNum } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Printer, ArrowLeft, CheckCheck, Send, X as XIcon, Trash2 } from "lucide-react";

export default function FloorPlanQuotationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [q, setQ] = useState(null);
  useEffect(() => { api.get(`/floorplan/quotations/${id}`).then((r) => setQ(r.data)); }, [id]);

  const change = async (status) => { const { data } = await api.patch(`/floorplan/quotations/${id}/status`, { status }); setQ(data); toast.success(`Marked ${status}`); };
  const del = async () => { if (!window.confirm("Delete this quotation?")) return; await api.delete(`/floorplan/quotations/${id}`); navigate("/floorplan-quotations"); };

  if (!q) return <Layout><div className="p-12 label-tech">Loading…</div></Layout>;

  return (
    <Layout>
      <div className="no-print">
        <PageHeader eyebrow={`Layout Quote · ${q.status}`} title={q.quote_number} subtitle={`Created ${new Date(q.created_at).toLocaleString("en-IN")} by ${q.created_by_name}`}
          actions={<div className="flex gap-2"><button onClick={() => navigate(-1)} className="btn-ghost"><ArrowLeft size={16}/> Back</button><button onClick={() => window.print()} className="btn-primary"><Printer size={16}/> Print / PDF</button></div>}
        />
        <div className="px-8 pt-6 flex flex-wrap items-center gap-2">
          <span className="label-tech mr-2">Actions</span>
          <button onClick={() => change("sent")} className="btn-secondary"><Send size={14}/> Mark Sent</button>
          <button onClick={() => change("approved")} className="btn-secondary"><CheckCheck size={14}/> Approved</button>
          <button onClick={() => change("rejected")} className="btn-secondary"><XIcon size={14}/> Rejected</button>
          {user.role === "admin" && <button onClick={del} className="btn-danger ml-auto"><Trash2 size={14}/> Delete</button>}
        </div>
      </div>

      <div className="p-8">
        <div className="panel-heavy max-w-5xl mx-auto" data-testid="fpq-pdf">
          <div className="p-8 border-b-2 border-ink flex items-start justify-between">
            <div className="flex items-start gap-5">
              <img src="/brand/adarsh-logo.png" alt="Adarsh Udyog" className="h-20 w-auto object-contain"/>
              <div>
                <div className="label-tech mt-1">A Complete Storage Solution</div>
                <div className="text-xs text-ink-muted mt-3 mono">
                  Plot No. 2, Nangli Sakrawati Industrial Area<br/>
                  adarshudyog@gmail.com · +91 98100 98860
                  {q.company_gstin && <><br/>GSTIN: {q.company_gstin}</>}
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="label-tech">Layout Quotation</div>
              <div className="mono font-bold text-2xl mt-1">{q.quote_number}</div>
              <div className="label-tech mt-4">Date</div>
              <div className="mono text-sm mt-1">{new Date(q.created_at).toLocaleDateString("en-IN")}</div>
              <div className="label-tech mt-4">Status</div>
              <div className="mono font-bold uppercase">{q.status}</div>
            </div>
          </div>

          <div className="p-8 grid grid-cols-2 gap-8 border-b border-line">
            <div>
              <div className="label-tech mb-3">Billed to</div>
              <div className="font-bold text-ink">{q.client?.company || "—"}</div>
              <div className="text-sm text-ink-muted">{q.client?.name}</div>
              <div className="text-sm text-ink-muted">{q.client?.contact}</div>
              <div className="text-sm text-ink-muted">{q.client?.address}</div>
              {q.client?.gstin && <div className="mono text-xs mt-2">GSTIN: {q.client.gstin}</div>}
            </div>
            <div>
              <div className="label-tech mb-3">Layout</div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <span className="text-ink-muted">Plan #</span><span className="mono font-bold text-right">{q.layout_plan_number}</span>
                <span className="text-ink-muted">Name</span><span className="mono font-bold text-right">{q.layout_name}</span>
                <span className="text-ink-muted">Area</span><span className="mono font-bold text-right">{fmtNum(q.area, 2)} {q.unit}²</span>
                <span className="text-ink-muted">Perimeter</span><span className="mono font-bold text-right">{fmtNum(q.perimeter, 2)} {q.unit}</span>
                <span className="text-ink-muted">Total items</span><span className="mono font-bold text-right">{q.total_items}</span>
                <span className="text-ink-muted">Total running ft</span><span className="mono font-bold text-right">{fmtNum(q.total_running_ft, 1)}</span>
                <span className="text-ink-muted">Total legs</span><span className="mono font-bold text-right">{q.total_legs ?? 0}</span>
              </div>
            </div>
          </div>

          <div className="p-8">
            <div className="label-tech mb-3">Bill of materials</div>
            <table className="w-full grid-lines">
              <thead><tr><th>Item</th><th>Type</th><th>Size</th><th className="num">Qty</th><th className="num">Legs</th><th className="num">Running ft</th><th className="num">Weight (kg)</th><th className="num">Rate</th><th className="num">Line total</th></tr></thead>
              <tbody>
                {q.lines.map((l, i) => (
                  <tr key={i}>
                    <td className="font-bold uppercase tracking-wider text-xs">{l.name}</td>
                    <td className="mono text-xs uppercase">{l.unit_type || "main"}</td>
                    <td className="mono text-xs">{l.unit_size}</td>
                    <td className="num">{l.quantity}</td>
                    <td className="num mono text-xs">{l.total_legs ?? 0}</td>
                    <td className="num mono text-xs">{fmtNum(l.total_running_ft, 1)}</td>
                    <td className="num mono text-xs">{fmtNum(l.total_weight_kg, 1)}</td>
                    <td className="num mono text-xs">₹{fmtNum(l.rate, 2)}{l.pricing_mode === "per_ft" ? "/ft" : ""}</td>
                    <td className="num font-bold">{fmtINR(l.line_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="p-8 border-t-2 border-ink">
            <div className="grid grid-cols-3 gap-6">
              <div>
                <div className="label-tech">Total weight</div>
                <div className="mono text-2xl font-bold mt-1">{fmtNum(q.total_weight_kg, 2)} kg</div>
              </div>
              <div>
                <div className="label-tech">Total items</div>
                <div className="mono text-2xl font-bold mt-1">{q.total_items}</div>
              </div>
              <div className="text-right space-y-1">
                <div className="flex justify-between text-sm"><span className="label-tech">Subtotal</span><span className="mono font-bold">{fmtINR(q.subtotal)}</span></div>
                <div className="flex justify-between text-sm"><span className="label-tech">GST @ {fmtNum(q.gst_percent, 0)}%</span><span className="mono font-bold">{fmtINR(q.gst_amount)}</span></div>
                <div className="flex justify-between pt-2 border-t-2 border-ink"><span className="label-tech">Grand Total</span><span className="display font-black text-3xl text-brand">{fmtINR(q.grand_total)}</span></div>
              </div>
            </div>
          </div>

          {q.notes && <div className="p-8 pt-0 border-t border-line"><div className="label-tech mb-2">Notes</div><div className="text-sm whitespace-pre-wrap">{q.notes}</div></div>}
          <div className="p-8 pt-4 border-t border-line text-xs text-ink-muted">Quotation valid 7 days from issue. Taxes as applicable.</div>
        </div>
      </div>
    </Layout>
  );
}
